import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";

const COPY = {
  cs: {
    title: "404 — Stránka nenalezena",
    heading: "Stránka neexistuje",
    body: "Adresa, kterou jste otevřel(a), na našem webu neexistuje. Pokračujte prosím na úvodní stránku nebo si prohlédněte naše produkty.",
    home: "Zpět na hlavní stránku",
    products: "Procházet produkty",
  },
  sk: {
    title: "404 — Stránka nenájdená",
    heading: "Stránka neexistuje",
    body: "Adresa, ktorú ste otvorili, na našom webe neexistuje. Pokračujte prosím na úvodnú stránku alebo si pozrite naše produkty.",
    home: "Späť na hlavnú stránku",
    products: "Prehľadávať produkty",
  },
  en: {
    title: "404 — Page Not Found",
    heading: "This page doesn't exist",
    body: "The address you opened does not exist on our website. Please go back to the homepage or browse our products.",
    home: "Back to homepage",
    products: "Browse products",
  },
  hu: {
    title: "404 — Az oldal nem található",
    heading: "Ez az oldal nem létezik",
    body: "A megadott cím nem található a weboldalunkon. Térjen vissza a kezdőlapra vagy nézze meg termékeinket.",
    home: "Vissza a főoldalra",
    products: "Termékek böngészése",
  },
};

export default function NotFound() {
  const { lang } = useI18n();
  const t = COPY[lang] || COPY.cs;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${t.title} • Správný Směr`;

    // Tell crawlers this isn't a real page (Google/Bing both honor this).
    const addOrUpdateMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev };
    };
    const robots = addOrUpdateMeta("robots", "noindex, nofollow");
    const prerender = addOrUpdateMeta("prerender-status-code", "404");

    // Best-effort: tell our origin server (via a query) that the resource is missing.
    // Some indexers respect a redirect to a real 404 endpoint.
    try {
      const url = window.location.pathname + window.location.search;
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      if (apiBase) {
        // Fire-and-forget ping so server logs have a record; ignored if it fails.
        fetch(`${apiBase}/api/404-log?path=${encodeURIComponent(url)}`).catch(() => {});
      }
    } catch (e) { /* ignore */ }

    return () => {
      document.title = prevTitle;
      if (robots.prev !== null) robots.el.setAttribute("content", robots.prev);
      else robots.el.remove();
      if (prerender.prev !== null) prerender.el.setAttribute("content", prerender.prev);
      else prerender.el.remove();
    };
  }, [t]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-20 bg-zinc-50">
      <div className="max-w-xl text-center" data-testid="not-found-page">
        <div className="font-display text-[8rem] sm:text-[10rem] font-extrabold tracking-tight leading-none bg-gradient-to-br from-blue-600 to-violet-600 bg-clip-text text-transparent select-none">
          404
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-900 mt-2 mb-4">
          {t.heading}
        </h1>
        <p className="text-zinc-600 mb-8 leading-relaxed">
          {t.body}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/"
            className="btn-primary px-6 py-3 rounded-full font-semibold"
            data-testid="not-found-home-btn"
          >
            {t.home}
          </Link>
          <Link
            to="/produkty"
            className="px-6 py-3 rounded-full font-semibold bg-white border border-zinc-200 text-zinc-900 hover:bg-zinc-100 transition"
            data-testid="not-found-products-btn"
          >
            {t.products}
          </Link>
        </div>
      </div>
    </div>
  );
}
